import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FaChevronDown, FaCheck } from 'react-icons/fa';
import { setCurrentLanguage, getCurrentLanguage, subscribeToLanguageChange } from '../../../utils/languageStore';

const langConfig = [
  { code: 'en', labelKey: 'english', dir: 'ltr' },
  { code: 'ar', labelKey: 'arabic', dir: 'rtl' },
  { code: 'ckb', labelKey: 'kurdish', dir: 'rtl' },
];

const dropdownVariants = {
  hidden: { opacity: 0, y: -5, scale: 0.98 },
  visible: { 
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 400, damping: 30 }
  },
  exit: { opacity: 0, y: -5, scale: 0.98, transition: { duration: 0.15 } }
};

export default function SwitchLang() {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLang, setCurrentLangState] = useState(getCurrentLanguage());
  const containerRef = useRef(null);

  useEffect(() => {
    const unsubscribe = subscribeToLanguageChange(newLang => {
      setCurrentLangState(newLang);
    });
    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  const changeLanguage = lang => {
    const selected = langConfig.find(l => l.code === lang);
    if (!selected) return;

    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
    document.documentElement.setAttribute('dir', selected.dir);
    setCurrentLangState(lang);
    setIsOpen(false);
  };

  const current = langConfig.find(l => l.code === currentLang) || langConfig[0];

  return (
    <div ref={containerRef} className="relative inline-block" dir={current.dir}>
      <button
        onClick={() => setIsOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-all text-sm font-medium bg-zinc-100/60 border-zinc-200/80 hover:bg-zinc-200/50 text-zinc-800 dark:bg-zinc-900/60 dark:border-zinc-800/80 dark:hover:bg-zinc-900 dark:text-zinc-200"
      >
        <span>{t(current.labelKey)}</span>
        <div className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`}>
          <FaChevronDown className="text-[10px] opacity-70" />
        </div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            variants={dropdownVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute top-full mt-2 z-50 start-0 min-w-[140px] p-1.5 rounded-2xl shadow-2xl backdrop-blur-xl bg-white/80 dark:bg-zinc-900/80 border border-zinc-200/80 dark:border-zinc-800/80"
          >
            <ul className="flex flex-col space-y-0.5">
              {langConfig.map(({ code, labelKey }) => {
                const isActive = code === currentLang;
                return (
                  <li key={code}>
                    <button
                      onClick={() => changeLanguage(code)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors duration-200 ${
                        isActive 
                          ? 'bg-zinc-100 dark:bg-white/10 text-zinc-900 dark:text-white font-semibold' 
                          : 'text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-white/5 hover:text-zinc-900 dark:hover:text-white'
                      }`}
                    >
                      <span className="truncate">{t(labelKey)}</span>
                      
                      {/* Active State Indicator */}
                      {isActive ? (
                        <FaCheck className="text-[10px] text-zinc-900 dark:text-white ml-2 rtl:ml-0 rtl:mr-2 shrink-0" />
                      ) : (
                        <div className="w-2.5 h-2.5 ml-2 rtl:ml-0 rtl:mr-2 shrink-0" /> // Placeholder to maintain alignment
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
